import { MouseEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { withSiteMeta } from '../HOCS/withSiteMeta';

const REAMAZE_SCRIPT_ID = 'reamaze-widget-script';
const REAMAZE_SCRIPT_SRC = 'https://cdn.reamaze.com/assets/reamaze.js';
const REAMAZE_POPUP_OPENED_EVENT = 'Reamaze.ShopifyCheckout.popupOpened';
const REAMAZE_POPUP_CLOSED_EVENT = 'Reamaze.ShopifyCheckout.popupClosed';
const REAMAZE_READY_TIMEOUT_MS = 15000;
export const REAMAZE_TRIGGER_ID = 'rio-reamaze-trigger';

declare global {
  interface Window {
    _rmzLoaded?: boolean;
    Reamaze?: {
      closePopup: () => void;
      popup: () => void;
      reload?: () => void;
    };
  }
}

let scriptLoadPromise: Promise<boolean> | null = null;
let loadPromise: Promise<boolean> | null = null;
let preparationPromise: Promise<boolean> | null = null;
let activationPromise: Promise<boolean> | null = null;
let normalizationPromise: Promise<boolean> | null = null;
let shouldReloadReamaze = false;
let isPrepared = false;
let hasActivationIntent = false;
let isExplicitlyOpen = false;
let isReplayingActivation = false;
let isOpenGuardInstalled = false;

const isReamazeInitialized = () => window._rmzLoaded === true;
const isReamazeRuntimeMounted = () => document.getElementById('reamaze-widget-container') !== null;
const isReamazePrepared = () => isPrepared && isReamazeRuntimeMounted();
const isReamazeOpen = () => document.querySelector('#reamaze-popup.reamaze-visible') !== null;

const waitForDomState = (isReady: () => boolean): Promise<boolean> => {
  if (isReady()) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    let observer: MutationObserver;
    const timeout = window.setTimeout(() => {
      observer.disconnect();
      resolve(isReady());
    }, REAMAZE_READY_TIMEOUT_MS);
    observer = new MutationObserver(() => {
      if (isReady()) {
        window.clearTimeout(timeout);
        observer.disconnect();
        resolve(true);
      }
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });
  });
};

const ensureReamazeScript = (): Promise<boolean> => {
  let script = document.getElementById(REAMAZE_SCRIPT_ID) as HTMLScriptElement | null;
  if (!script) {
    scriptLoadPromise = null;
  }

  if (typeof window.Reamaze?.popup === 'function' || script?.dataset.reamazeLoaded === 'true') {
    return Promise.resolve(true);
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise<boolean>((resolve) => {
    if (!script) {
      script = document.createElement('script');
      script.id = REAMAZE_SCRIPT_ID;
      script.src = REAMAZE_SCRIPT_SRC;
      script.async = true;
    }

    const cleanUp = () => {
      script?.removeEventListener('load', handleLoad);
      script?.removeEventListener('error', handleError);
    };
    const handleLoad = () => {
      cleanUp();
      if (script) {
        script.dataset.reamazeLoaded = 'true';
      }
      resolve(true);
    };
    const handleError = () => {
      cleanUp();
      script?.remove();
      scriptLoadPromise = null;
      resolve(false);
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    if (!script.isConnected) {
      document.body.appendChild(script);
    }
  });

  return scriptLoadPromise;
};

const waitForReamazeRuntime = () => waitForDomState(isReamazeRuntimeMounted);
const waitForReamazeClosed = () => waitForDomState(() => !isReamazeOpen());
const waitForReamazeOpen = () => waitForDomState(isReamazeOpen);

const waitForReamazeNormalization = async (): Promise<boolean> => {
  while (normalizationPromise) {
    if (!(await normalizationPromise)) {
      return false;
    }
  }
  return true;
};

const installReamazeOpenGuard = () => {
  if (isOpenGuardInstalled) {
    return;
  }

  document.addEventListener(REAMAZE_POPUP_OPENED_EVENT, () => {
    if (hasActivationIntent || isExplicitlyOpen) {
      return;
    }

    isPrepared = false;
    preparationPromise = null;
    window.Reamaze?.closePopup();
    if (!normalizationPromise) {
      normalizationPromise = waitForReamazeClosed()
        .then((closed) => {
          isPrepared = closed && isReamazeRuntimeMounted();
          return closed;
        })
        .finally(() => {
          normalizationPromise = null;
        });
    }
  });
  document.addEventListener(REAMAZE_POPUP_CLOSED_EVENT, () => {
    isExplicitlyOpen = false;
  });
  isOpenGuardInstalled = true;
};

export const loadReamaze = (): Promise<boolean> => {
  if (typeof document === 'undefined') {
    return Promise.resolve(false);
  }

  if (isReamazeInitialized()) {
    return Promise.resolve(true);
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<boolean>((resolve) => {
    let isSettled = false;
    let timeout = window.setTimeout(() => finish(false), REAMAZE_READY_TIMEOUT_MS);

    const finish = (loaded: boolean) => {
      if (isSettled) {
        return;
      }

      isSettled = true;
      window.clearTimeout(timeout);
      document.removeEventListener('Reamazeinit', handleInit);
      loadPromise = null;
      resolve(loaded);
    };
    const handleInit = () => {
      shouldReloadReamaze = false;
      finish(true);
    };

    document.addEventListener('Reamazeinit', handleInit, { once: true });
    void ensureReamazeScript().then((scriptLoaded) => {
      if (isSettled || !scriptLoaded) {
        finish(false);
        return;
      }

      if (isReamazeInitialized()) {
        finish(true);
        return;
      }

      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        shouldReloadReamaze = typeof window.Reamaze?.reload === 'function';
        finish(false);
      }, REAMAZE_READY_TIMEOUT_MS);

      if (shouldReloadReamaze && window.Reamaze?.reload) {
        shouldReloadReamaze = false;
        window.Reamaze.reload();
      }
    });
  });

  return loadPromise;
};

const prepareReamaze = (): Promise<boolean> => {
  installReamazeOpenGuard();

  if (isPrepared && !isReamazeRuntimeMounted()) {
    isPrepared = false;
    isExplicitlyOpen = false;
    preparationPromise = null;
  }

  if (isReamazePrepared()) {
    return Promise.resolve(true);
  }

  if (preparationPromise) {
    return preparationPromise;
  }

  isPrepared = false;
  preparationPromise = loadReamaze()
    .then(async (loaded) => {
      if (!loaded) {
        return false;
      }

      if (!(await waitForReamazeRuntime())) {
        return false;
      }

      if (!hasActivationIntent && isReamazeOpen()) {
        window.Reamaze?.closePopup();
        if (!(await waitForReamazeClosed())) {
          return false;
        }
      }

      if (!(await waitForReamazeNormalization())) {
        return false;
      }
      isPrepared = true;
      return true;
    })
    .finally(() => {
      if (!isPrepared) {
        preparationPromise = null;
      }
    });

  return preparationPromise;
};

export const openReamaze = (): Promise<boolean> => {
  if (typeof document === 'undefined') {
    return Promise.resolve(false);
  }

  if (activationPromise) {
    return activationPromise;
  }

  hasActivationIntent = true;
  activationPromise = prepareReamaze()
    .then(async (prepared) => {
      if (!prepared) {
        return false;
      }

      if (!(await waitForReamazeNormalization())) {
        return false;
      }
      const opened = waitForReamazeOpen();

      const trigger = document.getElementById(REAMAZE_TRIGGER_ID) as HTMLButtonElement | null;
      if (trigger) {
        isReplayingActivation = true;
        try {
          trigger.click();
        } finally {
          isReplayingActivation = false;
        }
      } else {
        window.Reamaze?.popup();
      }

      if (!(await opened)) {
        isExplicitlyOpen = false;
        window.Reamaze?.closePopup();
        return false;
      }
      isExplicitlyOpen = true;
      return true;
    })
    .finally(() => {
      hasActivationIntent = false;
      activationPromise = null;
    });

  return activationPromise;
};

type ReamazeWidgetProps = {
  siteConfig: {
    enableReamaze?: boolean;
  };
};

export const ReamazeWidgetComponent = ({ siteConfig }: ReamazeWidgetProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isCookieFooterDismissed, setIsCookieFooterDismissed] = useState(false);

  useEffect(() => {
    setIsCookieFooterDismissed(document.cookie.includes('rio-cfs'));
  }, []);

  if (!siteConfig.enableReamaze) {
    return null;
  }

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (isReplayingActivation) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (isReamazePrepared() && isReamazeOpen()) {
      isExplicitlyOpen = false;
      window.Reamaze?.closePopup();
      return;
    }

    setIsLoading(true);
    void openReamaze().finally(() => setIsLoading(false));
  };

  const handleMouseEnter = () => {
    if (!isReamazePrepared()) {
      void prepareReamaze();
    }
  };

  const label = t('common:support.open');

  return (
    <button
      id={REAMAZE_TRIGGER_ID}
      type="button"
      className="rio-reamaze-trigger"
      style={{ bottom: isCookieFooterDismissed ? '40px' : '75px' }}
      data-reamaze-lightbox="contact"
      data-reamaze-lightbox-anchored="true"
      aria-label={label}
      aria-busy={isLoading}
      title={label}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
    >
      <i className={isLoading ? 'fa fa-spinner fa-spin' : 'fas fa-question'} aria-hidden="true" />
    </button>
  );
};

export const ReamazeWidget = withSiteMeta(ReamazeWidgetComponent);
